import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { api } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Refresh under the server's 15-min idle window to avoid user-visible 401s.
const PROACTIVE_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

// Sentinel distinguishing "server said 401 (not logged in)" from "no user data".
const UNAUTHENTICATED = Symbol('unauthenticated');

/** Fetch the current user from /api/auth/me.
 * Returns the user object, UNAUTHENTICATED on a 401, or null for other statuses.
 * Behavior matches the original inline mount logic. */
async function fetchCurrentUser() {
  const resp = await fetch(`${API_URL}/api/auth/me`, {
    credentials: 'include', // Send cookies
  });
  if (resp.ok) return resp.json();
  if (resp.status === 401) return UNAUTHENTICATED;
  return null;
}

/** Fetch the tenant profile from /api/tenant/profile; returns null on any
 * failure (tenant fetch errors were previously swallowed). */
async function fetchTenantProfile() {
  try {
    const tenantResp = await fetch(`${API_URL}/api/tenant/profile`, {
      credentials: 'include',
    });
    if (tenantResp.ok) return await tenantResp.json();
  } catch { /* ignore tenant fetch errors */ }
  return null;
}

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditLocked, setCreditLocked] = useState(false);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [twoFAPending, setTwoFAPending] = useState(null);

  const checkCreditStatus = async () => {
    try {
      if (!user) return;
      const resp = await fetch(`${API_URL}/api/credits/check-status`, {
        credentials: 'include', // Send cookies
      });
      if (resp.ok) {
        const data = await resp.json();
        setCreditLocked(data.is_locked === true);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    // Cookie-based auth: fetch auth state from server on mount
    let cancelled = false;
    (async () => {
      try {
        const freshUser = await fetchCurrentUser();
        if (cancelled) return;
        if (freshUser === UNAUTHENTICATED) {
          // Not authenticated - clear state
          setUser(null);
          setTenant(null);
        } else if (freshUser) {
          setUser(freshUser);
          // Fetch tenant info if user has tenant_id
          if (freshUser.tenant_id) {
            const tenantData = await fetchTenantProfile();
            if (!cancelled && tenantData) setTenant(tenantData);
          }
        }
      } catch { /* network error - stay logged out */ }
      finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (user && !user.is_system_admin) {
      checkCreditStatus();
      refreshTenant();
      const interval = setInterval(refreshTenant, 60 * 1000);
      const onFocus = () => refreshTenant();
      window.addEventListener('focus', onFocus);
      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', onFocus);
      };
    }
  }, [user]);

  // Proactive session keep-alive on a timer and on tab focus.
  useEffect(() => {
    if (!user) return undefined;

    // Fire-and-forget; the 401 handler covers refresh failures.
    const keepAlive = () => {
      api.refreshSession().catch(() => { /* ignore; 401 flow handles failures */ });
    };

    const interval = setInterval(keepAlive, PROACTIVE_REFRESH_INTERVAL_MS);
    const onFocus = () => {
      if (document.visibilityState === 'visible') keepAlive();
    };
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [user]);

  const login = async (email, password, captcha_token) => {
    const response = await fetch(`${API_URL}/api/auth/login/json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Send/receive cookies
      body: JSON.stringify({
        email: typeof email === 'string' ? email.trim() : email,
        password: typeof password === 'string' ? password.trim() : password,
        captcha_token,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const detail = error.detail;
      let message = 'Login failed';
      if (typeof detail === 'string' && detail) message = detail;
      else if (Array.isArray(detail)) message = detail.map(e => e.msg || JSON.stringify(e)).join('; ');
      throw new Error(message);
    }

    const data = await response.json();

    // 2FA required — return partial response, don't set tokens yet
    if (data.requires_2fa) {
      setTwoFAPending({ twofa_token: data.twofa_token, email });
      return data;
    }

    _handleLoginSuccess(data);
    return data;
  };

  const verify2FA = async (twofa_token, otp, trust_device = false) => {
    const response = await fetch(`${API_URL}/api/auth/2fa/verify-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ twofa_token, otp, trust_device }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'OTP verification failed');
    }

    const data = await response.json();
    setTwoFAPending(null);
    _handleLoginSuccess(data);
    return data;
  };

  const cancelTwoFA = () => setTwoFAPending(null);

  const _handleLoginSuccess = (data) => {
    // Cookies are set by backend, just update state
    setUser(data.user);
    if (data.tenant) {
      setTenant(data.tenant);
    } else {
      setTenant(null);
    }
    if (data.password_expired) {
      setPasswordExpired(true);
    }
  };

  const _clearLocalAuthState = () => {
    setUser(null);
    setTenant(null);
    setCreditLocked(false);
    setPasswordExpired(false);
  };

  const logout = async () => {
    // Call backend logout (clears cookies + revokes session)
    try {
      // Read CSRF token from cookie
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('ub_csrf='))
        ?.split('=')[1];
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Send cookies
        headers,
      });
    } catch (err) {
      console.warn('[Auth] Logout request failed; clearing local state anyway:', err?.message);
    } finally {
      _clearLocalAuthState();
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const updateTenant = (updatedTenant) => {
    if (updatedTenant) {
      setTenant(updatedTenant);
    }
  };

  const refreshTenant = async () => {
    try {
      if (!user) return;
      const resp = await fetch(`${API_URL}/api/tenant/profile`, {
        credentials: 'include', // Send cookies
      });
      if (resp.ok) {
        const data = await resp.json();
        setTenant(data);
      }
    } catch { /* ignore */ }
  };

  const contextValue = useMemo(() => ({
    user, tenant, loading, login, verify2FA, cancelTwoFA, twoFAPending, logout, isAuthenticated: !!user, creditLocked, passwordExpired, setPasswordExpired, updateUser, updateTenant, refreshTenant
  }), [user, tenant, loading, twoFAPending, creditLocked, passwordExpired]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
